import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, Paperclip, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import Modal from '../../../shared/components/ui/Modal';
import { Callout, Field, SegmentedControl } from '../../../shared/components/ui/primitives';
import {
  getMySiteWalletProjects,
  getSiteWalletCategories,
  submitSiteWalletExpense,
  uploadFileToS3
} from '../../services/api/implementation/impl';
import { dayKey, errorMessage, inputClass, looksLikeMaterial, money } from './siteWalletUi';

const PAID_FROM = [
  { value: 'wallet', label: 'Wallet' },
  { value: 'own', label: 'Own pocket' },
  { value: 'split', label: 'Split' }
];

/** How far back an expense may be dated (MAX_BACKDATE_DAYS on the server). */
const MAX_BACKDATE_DAYS = 30;

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * S3 keys are the file name, so two phones' "IMG_0001.jpg" would overwrite
 * each other. Every bill goes up under a name of its own.
 */
function billFileName(file) {
  const name = String(file.name || '');
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot).toLowerCase().replace(/[^a-z0-9.]/g, '') : '';
  return `site-wallet-bill-${uuidv4()}${ext}`;
}

const emptyForm = () => ({
  amount: '',
  project_id: '',
  category_id: '',
  spent_on: dayKey(),
  note: '',
  paidFrom: 'wallet',
  walletPart: ''
});

/**
 * The holder's expense form, the web twin of the app's Add Expense, opened as a
 * modal from My Wallet.
 *
 * `client_ref` is made once per opening of the form, so a double click or a
 * retry after a dropped connection can't file the same expense twice; the
 * server returns the first one instead.
 */
const AddExpenseForm = ({ wallet, onClose, onDone }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [clientRef] = useState(() => uuidv4());
  const [bills, setBills] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [tried, setTried] = useState(false);

  const projects = useQuery({ queryKey: ['siteWalletMyProjects'], queryFn: getMySiteWalletProjects });
  const categories = useQuery({
    queryKey: ['siteWalletCategories', 'active'],
    queryFn: () => getSiteWalletCategories()
  });

  const available = Math.max(0, Number(wallet.available ?? 0));
  const amount = Number(form.amount);
  const amountOk = form.amount !== '' && Number.isFinite(amount) && amount > 0;

  // The split the server would take: Wallet is all wallet, Own pocket is all
  // own, Split is what was typed for the wallet and the rest from pocket.
  const split = useMemo(() => {
    if (!amountOk) return { wallet: 0, own: 0 };
    if (form.paidFrom === 'own') return { wallet: 0, own: round2(amount) };
    if (form.paidFrom === 'wallet') return { wallet: round2(amount), own: 0 };
    const w = Math.min(Math.max(0, Number(form.walletPart) || 0), amount);
    return { wallet: round2(w), own: round2(amount - w) };
  }, [amount, amountOk, form.paidFrom, form.walletPart]);

  /**
   * More than is available from the wallet: switch to Split with what the
   * wallet can cover, the rest from pocket (the server's suggestSplit).
   */
  const prefillSplit = (next) => {
    const n = Number(next.amount);
    if (next.paidFrom === 'wallet' && Number.isFinite(n) && n > available) {
      return { ...next, paidFrom: 'split', walletPart: String(round2(Math.min(n, available))) };
    }
    return next;
  };
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const category = (categories.data ?? []).find((c) => String(c.id) === String(form.category_id));
  const materialHint = looksLikeMaterial(category?.name) || looksLikeMaterial(form.note);
  const billRequiredAbove = Number(wallet.bill_required_above ?? 100);
  const needsBill = amountOk && amount > billRequiredAbove && bills.length === 0;
  const overWallet = split.wallet > available + 0.001;
  const frozen = wallet.status !== 'ACTIVE';

  const problems = {
    amount: amountOk ? null : 'Enter the amount',
    project_id: form.project_id ? null : 'Choose the project',
    category_id: form.category_id ? null : 'Choose a category',
    spent_on: form.spent_on ? null : 'Choose the date',
    bill: needsBill ? `Add a bill photo for anything over ${money(billRequiredAbove)}` : null,
    split: overWallet ? `Only ${money(available)} is available in the wallet` : null
  };
  const invalid = Object.values(problems).some(Boolean);

  const uploadBills = async (files) => {
    const list = Array.from(files || []).slice(0, 10 - bills.length);
    for (const file of list) {
      setUploading((n) => n + 1);
      try {
        const stored = new File([file], billFileName(file), { type: file.type });
        const response = await uploadFileToS3(stored);
        const url = response?.data?.files?.[0]?.presignedUrl;
        if (!url) throw new Error('The upload returned no file URL');
        setBills((b) => [...b, { name: file.name, url }]);
      } catch (err) {
        toast.error(`Could not upload ${file.name}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const submit = useMutation({
    mutationFn: submitSiteWalletExpense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['siteWalletMe'] });
      queryClient.invalidateQueries({ queryKey: ['siteWalletMyExpenses'] });
      queryClient.invalidateQueries({ queryKey: ['siteWalletMyLedger'] });
      toast.success('Expense submitted for approval');
      onDone?.();
    },
    onError: (err) => {
      // More than available: the server sends the split it would accept.
      const suggested = err?.response?.data?.data?.suggested_split;
      if (suggested) {
        queryClient.invalidateQueries({ queryKey: ['siteWalletMe'] });
        setForm((f) => ({ ...f, paidFrom: 'split', walletPart: String(suggested.wallet_amount) }));
      }
      toast.error(errorMessage(err, 'Could not submit the expense'));
    }
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setTried(true);
    if (invalid || frozen || uploading) return;
    submit.mutate({
      project_id: Number(form.project_id),
      category_id: Number(form.category_id),
      wallet_amount: split.wallet,
      own_amount: split.own,
      spent_on: form.spent_on,
      note: form.note.trim() || null,
      bill_urls: bills.map((b) => b.url),
      client_ref: clientRef
    });
  };

  const show = (key) => (tried ? problems[key] : null);

  return (
    <Modal
      name="Add expense"
      onClose={onClose}
      width={44}
      confirmOnClose
      isDirty={Boolean(form.amount || form.note || bills.length)}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <p className="text-sm text-gray-500">
          Available in the wallet: {money(available)}. Material goes through a material request, not here.
        </p>
        {frozen && (
          <Callout tone="warning">This wallet is frozen. Ask the builder to unfreeze it.</Callout>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Amount (₹)" required error={show('amount')}>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm((f) => prefillSplit({ ...f, amount: e.target.value }))}
              className={inputClass}
            />
          </Field>
          <Field
            label="Date spent"
            required
            error={show('spent_on')}
            hint={`Up to ${MAX_BACKDATE_DAYS} days back`}>
            <input
              type="date"
              value={form.spent_on}
              min={dayKey(-MAX_BACKDATE_DAYS)}
              max={dayKey()}
              onChange={set('spent_on')}
              className={inputClass}
            />
          </Field>
          <Field label="Project" required error={show('project_id')}>
            <select value={form.project_id} onChange={set('project_id')} className={inputClass}>
              <option value="">{projects.isLoading ? 'Loading…' : 'Choose a project'}</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Category" required error={show('category_id')}>
            <select value={form.category_id} onChange={set('category_id')} className={inputClass}>
              <option value="">{categories.isLoading ? 'Loading…' : 'Choose a category'}</option>
              {(categories.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {materialHint && (
          <Callout tone="warning">
            Material? Raise a material request instead.{' '}
            <Link to="/builder/inventory/requirements" className="font-medium underline">
              Go to Requirements
            </Link>
          </Callout>
        )}

        <Field label="Note">
          <textarea
            value={form.note}
            onChange={set('note')}
            rows={2}
            maxLength={2000}
            className={inputClass}
            placeholder="What it was for"
          />
        </Field>

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-600">Paid from</div>
          <SegmentedControl
            ariaLabel="Paid from"
            size="sm"
            value={form.paidFrom}
            onChange={(v) =>
              setForm((f) =>
                prefillSplit({
                  ...f,
                  paidFrom: v,
                  walletPart:
                    v === 'split' && f.walletPart === ''
                      ? String(round2(Math.min(Number(f.amount) || 0, available)))
                      : f.walletPart
                })
              )
            }
            options={PAID_FROM}
          />
          {form.paidFrom === 'split' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="From wallet (₹)" error={show('split')}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.walletPart}
                  onChange={set('walletPart')}
                  className={inputClass}
                />
              </Field>
              <Field label="From own pocket (₹)" hint="Accounts pays this back once approved">
                <input value={money(split.own)} readOnly className={`${inputClass} bg-gray-50`} />
              </Field>
            </div>
          )}
          {form.paidFrom === 'wallet' && show('split') && (
            <span className="text-sm text-danger">{problems.split}</span>
          )}
          {form.paidFrom === 'own' && (
            <p className="text-sm text-gray-500">Accounts pays this back to you once it is approved.</p>
          )}
        </div>

        <Field
          label="Bill"
          required={amountOk && amount > billRequiredAbove}
          error={show('bill')}
          hint={`Needed for anything over ${money(billRequiredAbove)}`}>
          <div className="flex flex-wrap items-center gap-2">
            {bills.map((b, i) => (
              <span
                key={b.url}
                className="inline-flex items-center gap-1 rounded-pill bg-gray-100 px-2 py-1 text-sm text-gray-700">
                <a href={b.url} target="_blank" rel="noreferrer" className="max-w-[160px] truncate hover:underline">
                  {b.name}
                </a>
                <button
                  type="button"
                  aria-label={`Remove ${b.name}`}
                  onClick={() => setBills((list) => list.filter((_, j) => j !== i))}>
                  <X size={12} />
                </button>
              </span>
            ))}
            {bills.length < 10 && (
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                {uploading ? 'Uploading…' : 'Add bill photo'}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    uploadBills(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        </Field>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="mr-auto rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700">
            Cancel
          </button>
          {amountOk && (
            <span className="text-sm text-gray-500">
              {money(split.wallet)} from wallet
              {split.own > 0 ? ` · ${money(split.own)} own pocket` : ''}
            </span>
          )}
          <button
            type="submit"
            disabled={submit.isPending || uploading > 0 || frozen}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {submit.isPending ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddExpenseForm;
